import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import CustomModal from "../../packages/CustomModal";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";

import ConfigPreview from "./ConfigPreview";
import FileSelectAndExtract from "./FileSelectAndExtract";

import {
  EXCLUDED_CHEMICAL_LIST,
  IMPORT_ALLOWED_CHEMICAL_CATEGORIES,
} from "../../constants/chemicals.constants";
import { CustomModalButtons } from "../../constants/global.constants";

import { delay } from "../../utils/Utils";

import { extractWaveProConfig } from "../../network/cipAPI";

import "./ImportPlantWavePro.scss";

const ImportPlantWavePro = ({
  show,
  onClose,
  noOfTrains,
  isEditFlow,
  applyWaveProConfig,
}) => {
  const { t } = useTranslation();
  const { showSuccessNotification } = useToastNotification();
  const { plantChemicalLibrary, userChemicalLibrary } = useSelector(
    (state) => state,
  );

  const [waveProConfig, setWaveProConfig] = useState(null);
  const [showExitImportConfirm, setShowExitImportConfirm] = useState(false);
  const [chemicalData, setChemicalData] = useState({
    chemicals: [],
    chemicalSelection: {},
  });
  const [normalizedSettingsAndCosts, setNormalizedSettingsAndCosts] =
    useState(null);
  const [trainCaseRoMap, setTrainCaseRoMap] = useState({});
  const [showLoader, setShowLoader] = useState(false);

  const shouldDisableImportConfig = useMemo(() => {
    const validTrainSelections = Object.values(trainCaseRoMap).filter(
      (selection) => Boolean(selection),
    );

    return validTrainSelections.length === 0;
  }, [trainCaseRoMap, plantChemicalLibrary]);

  const handleExtract = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await extractWaveProConfig(formData);
      if (response && response.status === 200) {
        if (response.casesWithRO > 0) {
          // to show full loader & success message
          setTimeout(() => {
            setWaveProConfig(response);
          }, 1000);
        } else {
          throw new Error(t("importPlantWavePro.extractNoRODesigns"));
        }
      } else {
        const message = response?.detail;
        throw new Error(message);
      }
    } catch (error) {
      return { error: error.message || t("importPlantWavePro.extractError") };
    }
  };

  const buildPendingChemicalChanges = () => {
    const chemicalsToBeAdded = [];
    const chemicalsToBeEdited = [];

    // To add/edit chemical call,
    // we consider the imported chemicals that are not in the EXCLUDED_CHEMICAL_LIST
    // and are in the allowed categories(acid, alkali, cip_additive).
    // we also add chemicals that are antiscalant, dechlorinator but will not display them in UI yet, might be added in future.
    const allowedImportChemicals = chemicalData.chemicals.filter(
      (chem) =>
        !EXCLUDED_CHEMICAL_LIST.some(
          (excludedChem) => excludedChem.symbol === chem.symbol,
        ) && IMPORT_ALLOWED_CHEMICAL_CATEGORIES.includes(chem.category),
    );

    // Counter for generating temporary chemical IDs for new imported chemicals
    let tempChemicalIdCounter = 0;

    allowedImportChemicals.forEach((importedChem) => {
      const existingUserChemical = userChemicalLibrary.find(
        (chem) =>
          chem.symbol === importedChem.symbol &&
          chem.bulk_concentration === importedChem.bulk_concentration,
      );

      let existingPlantChemical = null;
      if (isEditFlow) {
        existingPlantChemical = plantChemicalLibrary.find(
          (chem) =>
            chem.symbol === importedChem.symbol &&
            chem.bulk_concentration === importedChem.bulk_concentration,
        );
      }

      // ideally both(existingUserChemical & existingPlantChemical) should be same chemical but price & unit can be different.
      if (existingUserChemical || existingPlantChemical) {
        const chemicalToUpdate = existingUserChemical || existingPlantChemical;
        if (
          importedChem.price !== existingUserChemical?.price ||
          importedChem.unit !== existingUserChemical?.unit ||
          (existingPlantChemical &&
            (importedChem.price !== existingPlantChemical.price ||
              importedChem.unit !== existingPlantChemical.unit))
        ) {
          // update the existing chemical with the imported chemical data price & unit for user chemical library
          chemicalsToBeEdited.push({
            ...chemicalToUpdate,
            price: importedChem.price,
            unit: importedChem.unit,
          });
        }
      } else {
        // add the imported chemical as custom chemical to user chemical library & it will be added to plant chemical library through BE
        // Assign temporary ID since imported chemicals don't have chem_id yet
        tempChemicalIdCounter += 1;
        chemicalsToBeAdded.push({
          ...importedChem,
          is_custom: true,
          tempChemicalId: `CUSTOM_${tempChemicalIdCounter}`,
        });
      }
    });

    return { chemicalsToBeAdded, chemicalsToBeEdited };
  };

  const handleModalClose = async ({ target }) => {
    if (
      target?.id === CustomModalButtons.CLOSE ||
      target?.id === CustomModalButtons.CANCEL
    ) {
      if (waveProConfig && Object.keys(waveProConfig).length > 0) {
        setShowExitImportConfirm(true);
      } else {
        onClose?.();
      }
    } else if (target?.id === CustomModalButtons.CONFIRM) {
      setShowLoader(true);
      const pendingChemicalChanges = buildPendingChemicalChanges();

      applyWaveProConfig(
        waveProConfig,
        { ...chemicalData, ...pendingChemicalChanges },
        trainCaseRoMap,
        normalizedSettingsAndCosts,
      );
      // added some delay for RONF elements call
      await delay(1000);
      setShowLoader(false);
      onClose?.();
      showSuccessNotification(t("importPlantWavePro.importSuccess"));
    }
  };

  const handleExitImportConfirmClose = (action) => {
    setShowExitImportConfirm(false);

    if (action === CustomModalButtons.CONFIRM) {
      onClose?.();
    }
  };

  useEffect(() => {
    if (!noOfTrains) {
      return;
    }
    const intialTrainSelection = {};
    for (let i = 0; i < noOfTrains; i++) {
      intialTrainSelection[`train${i + 1}`] = null;
    }
    setTrainCaseRoMap(intialTrainSelection);
  }, [noOfTrains]);

  if (!show) return null;

  return (
    <>
      <CustomModal
        header={t("importPlantWavePro.modalTitle")}
        headerHelper={
          !waveProConfig ? t("importPlantWavePro.modalHelper") : null
        }
        onModalClose={handleModalClose}
        width="1050px"
        hideFooter={!waveProConfig}
        custombodyclass="import-plant-config-modal-body"
        confirmBtn={t("importPlantWavePro.importConfig")}
        confirmBtnDisabled={shouldDisableImportConfig}
        isLoading={showLoader}
      >
        <div className="import-plant-config-content">
          {waveProConfig ? (
            <ConfigPreview
              config={waveProConfig}
              chemicalData={chemicalData}
              setChemicalData={setChemicalData}
              trainCaseRoMap={trainCaseRoMap}
              setTrainCaseRoMap={setTrainCaseRoMap}
              setNormalizedSettingsAndCosts={setNormalizedSettingsAndCosts}
            />
          ) : (
            <FileSelectAndExtract onExtract={handleExtract} />
          )}
        </div>
      </CustomModal>

      {showExitImportConfirm && (
        <ConfirmationPopup
          type={DIALOG_TYPES.WARNING}
          header={t("importPlantWavePro.exitImportConfirmHeader")}
          description={t("importPlantWavePro.exitImportConfirmDescription")}
          cancelBtn={t("importPlantWavePro.exitImportCancel")}
          confirmBtn={t("importPlantWavePro.exitImportProceed")}
          onClose={handleExitImportConfirmClose}
        />
      )}
    </>
  );
};

export default ImportPlantWavePro;
